import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import PropTypes from 'prop-types';
import { colors } from '../../style/rulesEngine';
import RuleCard from './RuleCard';

const RulesList = ({ rules, onEdit, onToggle, onDelete, onDuplicate, onReorder, onCreateNew }) => {
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showInfoTooltip, setShowInfoTooltip] = useState(false);
  const dragIndexRef = useRef(null);

  // React Native Web's View does NOT forward the `draggable` prop or fire
  // drag events. We bypass this entirely by attaching native DOM listeners
  // directly on the elements identified by nativeID.
  useEffect(() => {
    const cleanups = rules.map((_, index) => {
      const el = document.getElementById(`rule-zone-${index}`);
      if (!el) return null;

      // Make the whole card zone draggable via native DOM
      el.setAttribute('draggable', 'true');
      el.style.cursor = 'grab';

      const onDragStart = (e) => {
        e.dataTransfer.setData('text/plain', String(index));
        e.dataTransfer.effectAllowed = 'move';
        dragIndexRef.current = index;
        el.style.opacity = '0.5';
      };

      const onDragEnd = () => {
        el.style.opacity = '1';
        setDragOverIndex(null);
        dragIndexRef.current = null;
      };

      const onDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
      };

      const onDragLeave = (e) => {
        if (!el.contains(e.relatedTarget)) {
          setDragOverIndex((prev) => (prev === index ? null : prev));
        }
      };

      const onDrop = (e) => {
        e.preventDefault();
        const fromIndex = dragIndexRef.current;
        if (fromIndex === null || fromIndex === index) {
          setDragOverIndex(null);
          dragIndexRef.current = null;
          return;
        }
        const ids = rules.map((r) => r.id);
        const [movedId] = ids.splice(fromIndex, 1);
        ids.splice(index, 0, movedId);
        onReorder(ids);
        setDragOverIndex(null);
        dragIndexRef.current = null;
      };

      el.addEventListener('dragstart', onDragStart);
      el.addEventListener('dragend', onDragEnd);
      el.addEventListener('dragover', onDragOver);
      el.addEventListener('dragleave', onDragLeave);
      el.addEventListener('drop', onDrop);

      return () => {
        el.removeEventListener('dragstart', onDragStart);
        el.removeEventListener('dragend', onDragEnd);
        el.removeEventListener('dragover', onDragOver);
        el.removeEventListener('dragleave', onDragLeave);
        el.removeEventListener('drop', onDrop);
      };
    });

    return () => cleanups.forEach((cleanup) => cleanup?.());
  }, [rules, onReorder]);

  const handleMove = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= rules.length) return;
    const ids = rules.map((r) => r.id);
    const [movedId] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, movedId);
    onReorder(ids);
  };

  return (
    <View>
      {rules.map((rule, index) => (
        <View
          key={rule.id}
          nativeID={`rule-zone-${index}`}
          style={[
            { marginVertical: 1 },
            dragOverIndex === index && {
              borderTopWidth: 2,
              borderTopColor: colors.accent,
              borderRadius: 2,
            },
          ]}
        >
          <RuleCard
            rule={rule}
            isActive={dragOverIndex === index}
            onEdit={onEdit}
            onToggle={onToggle}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onMoveUp={index > 0 ? () => handleMove(index, index - 1) : null}
            onMoveDown={index < rules.length - 1 ? () => handleMove(index, index + 1) : null}
          />
        </View>
      ))}

      {/* Add a Rule — centered, with info icon and + button */}
      <View style={{ alignItems: 'center', paddingTop: 24 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, position: 'relative' }}>
          <TouchableOpacity onPress={onCreateNew}>
            <Text style={{
              fontFamily: 'Poppins_600SemiBold',
              color: '#DBDADA',
              fontSize: 16,
            }}>
              Add a Rule
            </Text>
          </TouchableOpacity>

          {/* Info icon */}
          <TouchableOpacity
            onPress={() => setShowInfoTooltip(!showInfoTooltip)}
            style={{ padding: 2 }}
          >
            <i className="icon-info-circled" style={{ color: colors.textMuted, fontSize: 16 }} />
          </TouchableOpacity>

          {/* Tooltip bubble */}
          {showInfoTooltip && (
            <LinearGradient
              colors={['#8a9daf', '#d7e1e9']}
              locations={[0, 1]}
              style={{
                position: 'absolute',
                bottom: 28,
                left: '50%',
                transform: 'translateX(-50%)',
                padding: 10,
                borderRadius: 5,
                width: 260,
                zIndex: 99,
              }}
            >
              <Text style={{ color: '#1a2a3a', fontFamily: 'Poppins_500Medium', fontSize: 12, lineHeight: 18 }}>
                Create a rule to automatically apply actions to matching orders when they are imported.
              </Text>
              <View style={{
                position: 'absolute',
                bottom: -10,
                left: '50%',
                marginLeft: -10,
                borderLeftWidth: 10,
                borderLeftColor: 'transparent',
                borderRightWidth: 10,
                borderRightColor: 'transparent',
                borderTopWidth: 10,
                borderTopColor: '#d7e1e9',
              }} />
            </LinearGradient>
          )}
        </View>

        <TouchableOpacity onPress={onCreateNew}>
          <i className="icon-plus" style={{ color: '#DBDADA', fontSize: 28 }} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

RulesList.propTypes = {
  rules: PropTypes.arrayOf(PropTypes.object).isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onCreateNew: PropTypes.func.isRequired,
};

export default RulesList;
